import Decimal from 'decimal.js';
import { isEmpty, isUndefined } from 'lodash';

import { BankIcon } from '@/components/icons/BankIcon';
import { CalendarIcon } from '@/components/icons/CalendarIcon';
import { CheckVerified02Icon } from '@/components/icons/CheckVerified02Icon';
import { CurrencyDollarIcon } from '@/components/icons/CurrencyDollarIcon';
import { FileCheck02Icon } from '@/components/icons/FileCheck02Icon';
import { MarkerPin03Icon } from '@/components/icons/MarkerPin03Icon';
import { NavigationPointer02Icon } from '@/components/icons/NavigationPointer02Icon';
import { ShieldTickIcon } from '@/components/icons/ShieldTickIcon';
import { User03Icon } from '@/components/icons/User03Icon';
import { MiniTableRow } from '@/components/tables/MiniTable/MiniTable';
import { getValuationDetailsFromEntity } from '@/modules/entities/details/EntityValuation/EntityValuation.utils';
import {
  EntityDetail_EntityFragment,
  EntityDetail_InsurancePolicyFragment,
} from '@/modules/entities/details/graphql/EntityDetailPage.generated';
import { GetAssetLocationDisplayOutput } from '@/modules/entities/utils/getAssetLocationDisplay';
import { EntityKind } from '@/types/schema';
import { sumDecimalJS } from '@/utils/decimalJSUtils';
import { UnreachableError } from '@/utils/errors';
import {
  formatCurrency,
  formatCurrencyMillions,
} from '@/utils/formatting/currency';
import { formatDateToMonDDYYYY } from '@/utils/formatting/dates';
import { formatTaxId } from '@/utils/formatting/strings';

import {
  EntityCoverSlideData,
  EntityCoverSlideDataPoint,
  EntityCoverSlideDetailsList,
  EntityCoverSlideDetailsListVariant,
} from './EntityCoverSlide.types';
import { getPersonalAccountDetails } from './EntityCoverSlide.utils.accounts';
import { getBusinessEntityDetails } from './EntityCoverSlide.utils.business';
import { getNonTrustCharitableEntityDetails } from './EntityCoverSlide.utils.nonTrustCharitable';
import {
  getTimeBoundTrustDetails,
  getTrustDetails,
} from './EntityCoverSlide.utils.trust';

export function getEntityCoverSlideData(
  entity: EntityDetail_EntityFragment
): EntityCoverSlideData {
  const title = entity.subtype.displayName;
  switch (entity.kind) {
    // Trusts
    case EntityKind.IrrevocableTrust:
    case EntityKind.RevocableTrust:
    case EntityKind.SlatTrust:
    case EntityKind.IlitTrust:
      return {
        title,
        detailsListsVariant: EntityCoverSlideDetailsListVariant.TwoColumn,
        ...getTrustDetails(entity, entity.kind),
      };

    // Time-bound entities
    case EntityKind.CrtTrust:
    case EntityKind.CltTrust:
    case EntityKind.QprtTrust:
    case EntityKind.GratTrust:
      return {
        title,
        detailsListsVariant: EntityCoverSlideDetailsListVariant.ThreeColumn,
        ...getTimeBoundTrustDetails(entity, entity.kind),
      };

    // Personal Accounts
    case EntityKind.IndividualPersonalAccount:
    case EntityKind.JointPersonalAccount:
    case EntityKind.InsurancePersonalAccount:
    case EntityKind.QualifiedTuitionPersonalAccount:
    case EntityKind.RetirementPersonalAccount:
    case EntityKind.CustodialPersonalAccount:
      return {
        title,
        detailsListsVariant: EntityCoverSlideDetailsListVariant.TwoColumn,
        ...getPersonalAccountDetails(entity, entity.kind),
      };

    // Non-trust charitable entities
    case EntityKind.DonorAdvisedFund:
    case EntityKind.PrivateFoundation:
      return {
        title,
        detailsListsVariant: EntityCoverSlideDetailsListVariant.TwoColumn,
        ...getNonTrustCharitableEntityDetails(entity, entity.kind),
      };

    // Business entities
    case EntityKind.LlcBusinessEntity:
    case EntityKind.SoleProprietorshipBusinessEntity:
    case EntityKind.ScorpBusinessEntity:
    case EntityKind.CcorpBusinessEntity:
    case EntityKind.GpBusinessEntity:
    case EntityKind.LpBusinessEntity:
      return {
        title,
        detailsListsVariant: EntityCoverSlideDetailsListVariant.OneColumn,
        ...getBusinessEntityDetails(entity, entity.kind),
      };

    case undefined:
      throw new Error('Entity subtype is undefined');

    default: {
      throw new UnreachableError({
        case: entity.kind,
        message: `Unhandled entity kind: ${entity.kind}`,
      });
    }
  }
}

/**
 * Returns the subheading shown above the entity name on the entity cover slide.
 * Donor advised funds are spelled out; all other entities use their extended
 * display kind.
 */
export function getEntityCoverSlideSubheading(
  entity: EntityDetail_EntityFragment
): string {
  if (entity.kind === EntityKind.DonorAdvisedFund) {
    return 'Donor advised fund';
  }
  return entity.extendedDisplayKind;
}

export const dataPointGetters = {
  getTotalMarketValueDataPoint,
  getAssetLocationDataPoint,
  getJurisdictionDataPoint,
  getNamesDataPoint,
  getTaxStatusDataPoint,
  getEffectiveDateDataPoint,
  getTaxIdDataPoint,
  getCTAReportingDataPoint,
  getFinCenIDDataPoint,
};

export const detailsListGetters = {
  getControllingPersons,
};

function getTotalMarketValueDataPoint(
  entity: EntityDetail_EntityFragment | null | undefined
): EntityCoverSlideDataPoint | null {
  if (!entity) return null;
  const { headlineValueLabel, headlineValueDecimal } =
    getValuationDetailsFromEntity(entity, {
      isViewOnly: true,
    });

  let value = formatCurrency(headlineValueDecimal);
  if (headlineValueDecimal.greaterThan('1_000_000')) {
    value = `${formatCurrencyMillions(headlineValueDecimal)}M`;
  }

  return {
    label: headlineValueLabel,
    value,
    Icon: CurrencyDollarIcon,
  };
}

function getAssetLocationDataPoint({
  assetLocationDetails,
}: {
  assetLocationDetails?: GetAssetLocationDisplayOutput | null | undefined;
}): EntityCoverSlideDataPoint | null {
  if (!assetLocationDetails?.locationDisplay) return null;

  // Prefer the simplified label (drops the redundant "(Federal & State)"
  // qualifier when federal and state statuses match); fall back to the full
  // display otherwise.
  const value =
    assetLocationDetails.locationDisplaySimplified ??
    assetLocationDetails.locationDisplay;

  return {
    label: 'Asset location',
    value,
    Icon: NavigationPointer02Icon,
  };
}

function getJurisdictionDataPoint({
  trustJurisdiction,
  formationJurisdiction,
}: {
  trustJurisdiction?: string | null | undefined;
  formationJurisdiction?: string | null | undefined;
}): EntityCoverSlideDataPoint | null {
  const jurisdiction = trustJurisdiction || formationJurisdiction;
  if (!jurisdiction) return null;
  return {
    label: trustJurisdiction ? 'Governing law' : 'Jurisdiction',
    value: jurisdiction,
    Icon: MarkerPin03Icon,
  };
}

function getNamesDataPoint(
  {
    grantorNamesShort,
    ownerNamesShort,
    donorNamesShort,
  }: {
    grantorNamesShort?: string | null | undefined;
    ownerNamesShort?: string | null | undefined;
    donorNamesShort?: string | null | undefined;
  },
  overrideLabel?: 'grantors' | 'owners' | 'donors'
): EntityCoverSlideDataPoint | null {
  const value = grantorNamesShort || ownerNamesShort || donorNamesShort;

  let label: string | null = null;
  if (overrideLabel === 'grantors') {
    label = 'Grantor(s)';
  } else if (overrideLabel === 'owners') {
    label = 'Owner(s)';
  } else if (overrideLabel === 'donors') {
    label = 'Donor(s)';
  } else if (grantorNamesShort) {
    label = 'Grantor(s)';
  } else if (ownerNamesShort) {
    label = 'Owner(s)';
  } else if (donorNamesShort) {
    label = 'Donor(s)';
  }

  if (!value || !label) return null;

  return {
    label,
    value,
    Icon: User03Icon,
  };
}

function getTaxStatusDataPoint({
  taxStatus,
}: {
  taxStatus?:
    | { value?: string | undefined; valueSecondary?: string | undefined }
    | undefined;
}): EntityCoverSlideDataPoint | null {
  if (!taxStatus?.value) return null;

  return {
    label: 'Tax status',
    value: taxStatus.value,
    Icon: BankIcon,
  };
}

function getEffectiveDateDataPoint({
  effectiveDate,
}: {
  effectiveDate?: Date | null | undefined;
}): EntityCoverSlideDataPoint | null {
  if (!effectiveDate) return null;
  return {
    label: 'Effective date',
    value: formatDateToMonDDYYYY(effectiveDate),
    Icon: CalendarIcon,
  };
}

function getTaxIdDataPoint({
  taxId,
}: {
  taxId?: string | undefined;
}): EntityCoverSlideDataPoint | null {
  if (!taxId) return null;
  return {
    label: 'Tax ID (EIN)',
    value: formatTaxId(taxId),
    Icon: ShieldTickIcon,
  };
}

function getCTAReportingDataPoint({
  requiresCtaReporting,
}: {
  requiresCtaReporting?: boolean | null | undefined;
}): EntityCoverSlideDataPoint | null {
  if (isUndefined(requiresCtaReporting)) return null;

  return {
    label: 'CTA Reporting',
    value: requiresCtaReporting ? 'Yes' : 'No',
    Icon: FileCheck02Icon,
  };
}

function getFinCenIDDataPoint({
  finCenID,
}: {
  finCenID?: string | null | undefined;
}): EntityCoverSlideDataPoint | null {
  if (!finCenID) return null;
  return {
    label: 'FinCEN ID',
    value: finCenID,
    Icon: CheckVerified02Icon,
  };
}

export function getTrimmedListForEntityCoverSlide(
  array: MiniTableRow[]
): MiniTableRow[] {
  if (isEmpty(array))
    return [
      {
        value: '',
        label: 'None specified',
      },
    ];

  // if we don't need to make space for the "and X more" entry, return the original array
  if (array.length <= MAX_PER_ENTITY_COVER_SLIDE_LIST + 1) {
    return array;
  }

  const outputArray = array.slice(0, MAX_PER_ENTITY_COVER_SLIDE_LIST - 1);
  outputArray.push({
    value: `and ${array.length - MAX_PER_ENTITY_COVER_SLIDE_LIST} more`,
    label: '',
  });
  return outputArray;
}

const MAX_PER_ENTITY_COVER_SLIDE_LIST = 5 as const;
function getControllingPersons({
  controllingPartyNames,
}: {
  controllingPartyNames?: string[] | null | undefined;
}): EntityCoverSlideDetailsList | null {
  if (!controllingPartyNames) return null;

  const entries = controllingPartyNames.map<MiniTableRow>((name) => ({
    label: name,
    value: '',
  }));

  return {
    title: 'Controlling persons/signatories',
    entries: getTrimmedListForEntityCoverSlide(entries),
  };
}

export function getTotalDeathBenefit({
  insurancePolicies,
}: {
  insurancePolicies?: EntityDetail_InsurancePolicyFragment[] | null | undefined;
}): Decimal {
  return sumDecimalJS(
    insurancePolicies?.map((policy) => policy.deathBenefitAmount) ?? []
  );
}
